import { useState, useEffect, useRef } from 'react'

import { codingWindow, isStudySession } from '../study/studyConfig'
import { useServerNow } from '../study/useServerNow'

// A classroom-study condition's countdown at serverNowMs, in seconds, or null to hide it:
// while the row is active (0:00 once the time is up, like every other session), and while it
// is closed before the end of the coding window. The instructor's page closes the row when its
// own countdown runs out, on the instructor's computer clock - possibly a little before the
// coding time is up on the server's - and a pause closes it too, while the coding carries on
// (studyConfig.codingTimeIsUp, useStudyPhase): the student keeps coding until that end.
const studySecondsLeft = (status, coding, serverNowMs) => {
  if (!coding) return null
  const msLeft = coding.endsAtMs - serverNowMs
  const shows = status === 'active' || (status === 'closed' && msLeft > 0)
  return shows ? Math.max(0, Math.round(msLeft / 1000)) : null
}

/**
 * Top-right countdown for the instructor-controlled session timer (see SessionLobby.jsx),
 * distinct from TaskTimerDisplay.jsx's separate CHI-study per-task clock. Computed from
 * session.started_at + session.duration_minutes rather than local state, so it matches
 * what the instructor sees and survives a page refresh - and re-derives on every session
 * change (status flips via the 10s SessionContext poll/websocket), so pausing or stopping
 * the session hides it without the student needing to reload.
 *
 * Renders nothing for a session that has never used the timer (duration_minutes null) or
 * isn't currently active.
 *
 * A classroom-study condition (study/studyConfig.js) counts down on the server's clock
 * instead (useServerNow), the one its coding time ends on - the probes and the delayed AI
 * unlock go by it too - so a student's wrong computer clock does not show a different end.
 * See studySecondsLeft for when it shows.
 */
export default function SessionTimerDisplay({ session }) {
  const [remaining, setRemaining] = useState(null)
  const intervalRef = useRef(null)
  const isStudy = isStudySession(session)
  const studyCoding = isStudy ? codingWindow(session) : null
  const serverNowMs = useServerNow(studyCoding !== null)

  useEffect(() => {
    clearInterval(intervalRef.current)
    if (isStudy || session?.status !== 'active' || session?.duration_minutes == null || !session?.started_at) {
      setRemaining(null)
      return
    }
    const startedAtMs = new Date(session.started_at).getTime()
    const durationSeconds = session.duration_minutes * 60
    const tick = () => {
      const elapsed = (Date.now() - startedAtMs) / 1000
      setRemaining(Math.max(0, Math.round(durationSeconds - elapsed)))
    }
    tick()
    intervalRef.current = setInterval(tick, 1000)
    return () => clearInterval(intervalRef.current)
  }, [isStudy, session?.status, session?.started_at, session?.duration_minutes])

  const secondsLeft = isStudy ? studySecondsLeft(session?.status, studyCoding, serverNowMs) : remaining
  if (secondsLeft == null) return null

  const minutes = Math.floor(secondsLeft / 60)
  const seconds = secondsLeft % 60

  return (
    <div style={{
      fontSize: '13px',
      fontWeight: 600,
      color: secondsLeft <= 60 ? '#fecaca' : '#e5e7eb',
      fontFamily: 'Monaco, Menlo, monospace',
      background: 'rgba(255, 255, 255, 0.1)',
      padding: '4px 10px',
      borderRadius: '6px',
    }}>
      ⏱ {minutes}:{seconds.toString().padStart(2, '0')}
    </div>
  )
}
